import DeleteIcon from "@mui/icons-material/Delete";
import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { format } from "date-fns";
import PropTypes from "prop-types";
import * as React from "react";
import { useEffect, useState } from "react";
import {
  retrieveAllFreightOffersApi,
  retrieveFilteredFreightOffersApi,
} from "../../../components/api/FreightOfferApiService";
import ExportExcel from "../../../utils/excelExport";
import { incoterms, portsOfDischarge, portsOfLoading, recordStatus } from "../../../utils/menu-items";
import AddFreightOffer from "./AddFreightOffer";
import EditFreightOffer from "./EditFreightOffer";
import { set } from "lodash";
import MyFilters from "../../../components/MyFilters";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "editButton",
    disablePadding: true,
    label: "",
    width: 45,
  },
  {
    id: "id",
    disablePadding: true,
    label: "Id",
  },
  {
    id: "forwarder",
    numeric: false,
    disablePadding: true,
    label: "Forwarder",
  },
  {
    id: "pointOfLoading",
    numeric: false,
    disablePadding: true,
    label: "Point Of Loading",
  },
  {
    id: "pointOfDischarge",
    numeric: false,
    disablePadding: true,
    label: "Point Of Discharge",
  },
  {
    id: "terms",
    numeric: false,
    disablePadding: true,
    label: "Terms",
  },
  {
    id: "twentyFtRate",
    numeric: false,
    disablePadding: true,
    label: "20\"",
  },
  {
    id: "fortyFtRate",
    numeric: true,
    disablePadding: true,
    label: "40\"",
  },
  {
    id: "fortyHcRate",
    numeric: true,
    disablePadding: true,
    label: "40HC",
  },
  {
    id: "ftl",
    disablePadding: true,
    label: "FTL",
  },
  {
    id: "validFrom",
    disablePadding: true,
    label: "Valid From",
  },
  {
    id: "validTo",
    disablePadding: true,
    label: "Valid To",
  },
  {
    id: "transitTime",
    numeric: true,
    disablePadding: true,
    label: "Transit Time",
  },
  {
    id: "remark",
    numeric: false,
    disablePadding: true,
    label: "Remark",
  },
  {
    id: "carrier",
    numeric: false,
    disablePadding: true,
    label: "Carrier",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: true,
    label: "Status",
    width: 85,
  },
  {
    id: "creationTime",
    numeric: false,
    disablePadding: true,
    label: "Creation Time",
  },
  {
    id: "createdBy",
    numeric: false,
    disablePadding: true,
    label: "Created By",
  },
  {
    id: "recordStatus",
    numeric: false,
    disablePadding: true,
    label: "Record Status",
  },
  {
    id: "editingPermission",
    numeric: false,
    disablePadding: true,
    label: "Edit Status",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"center"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected, showFilters, setShowFilters, rows, setAddView } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity,
            ),
        }),
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <Stack direction="row" spacing={1} alignItems={"center"}>
        <Typography
          sx={{ fontWeight: 600, fontSize: "20px" }}
          variant="h6"
          id="tableTitle"
          component="div"
          color="#1E2685"
        >
          Freight Offers
        </Typography>
        <Typography></Typography>
        {numSelected > 0 ? (
          <Typography
            sx={{ fontWeight: 200, fontSize: "16px" }}
            color="#1E2685"
            variant="subtitle1"
            component="div"
          >
            {numSelected} selected
          </Typography>
        ) : (
          <Typography></Typography>
        )}

        {numSelected > 0 && (
          <Tooltip title="Delete">
            <IconButton>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack spacing={1} direction="row" sx={{ mr: 2 }}>
        <ExportExcel
          excelData={rows}
          fileName={"ExcelExport"}
          buttonLabel="Export Table"
        />

        <Button
          disableElevation
          variant="outlined"
          size="small"
          onClick={() => setAddView(true)}
        >
          Add
        </Button>
        <Button
          variant="outlined"
          onClick={() => setShowFilters(!showFilters)}
          sx={{ minWidth: "130px", mr: 2 }}
          size="small"
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </Stack>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function FreightOfferView() {
  const [rows, setRows] = useState([]);
  useEffect(() => retrieveFreightOffers(), []);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [addView, setAddView] = useState(false);
  const [addViewStates, setAddViewStates] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const [filterFreightOffer, setFilterFreightOffer] = useState({
    id: 0,
    pointOfLoadingList: [],
    pointOfDischargeList: [],
    termsList: [],
    statusList: [],
    fortyFtCurrencyList: [],
    twentyFtCurrencyList: [],
    thirdFtCurrencyList: [],
    carrier: "",
    remark: "",
    forwarderList: [],
    recordStatusList: ["ACTIVE"],
  });

  function retrieveFreightOffers() {
    retrieveAllFreightOffersApi()
      .then((response) => {
        setRows(response?.data);
      })
      .catch((error) => console.log(error.response?.data));
  }

  const [editFreightOffer, setEditFreightOffer] = useState({
    visible: false,
    data: null,
    index: null,
  });

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }else{
      setSelected([]);
    }
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, rows, editFreightOffer],
  );

  return (
    <div>
      {addView ? (
        <AddFreightOffer
          setAddViewStates={setAddViewStates}
          addViewStates={addViewStates}
          rows={rows}
          setRows={setRows}
          setAddView={setAddView}
        />
      ) : editFreightOffer.visible ? (
        <EditFreightOffer
          rows={rows}
          setRows={setRows}
          setAddView={setAddView}
          editFreightOffer={editFreightOffer}
          setEditFreightOffer={setEditFreightOffer}
        />
      ) : (
        <>
          {showFilters && (
            <Box
              component="form"
              sx={{
                "& .MuiTextField-root": { m: 1, width: "25ch" },
              }}
              noValidate
              autoComplete="off"
            >
              <Grid
                container
                spacing={1}
                justifyContent="flex-start"
                alignItems="center"
              >

                <TextField
                  id="forwarder"
                  label="Freight Forwarder"
                  value={filterFreightOffer?.forwarder || ""}
                  type="text"
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      forwarder: e.target.value,
                    })
                  }
                />

                <TextField
                  type="search"
                  id="outlined-select-pol"
                  select
                  fullWidth
                  label="Point Of Loading"
                  SelectProps={{
                    multiple: true,
                    value: filterFreightOffer.pointOfLoadingList || [],
                    onChange: (e) => {
                      setFilterFreightOffer({
                        ...filterFreightOffer,
                        pointOfLoadingList: e.target.value,
                      });
                    },
                  }}
                >
                  {portsOfLoading.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="search"
                  id="outlined-select-pod"
                  select
                  fullWidth
                  label="Point Of Discharge"
                  SelectProps={{
                    multiple: true,
                    value: filterFreightOffer.pointOfDischargeList || [],
                    onChange: (e) => {
                      setFilterFreightOffer({
                        ...filterFreightOffer,
                        pointOfDischargeList: e.target.value,
                      });
                    },
                  }}
                >
                  {portsOfDischarge.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="search"
                  id="outlined-select-terms"
                  select
                  fullWidth
                  label="Terms"
                  SelectProps={{
                    multiple: true,
                    value: filterFreightOffer.termsList || [],
                    onChange: (e) => {
                      setFilterFreightOffer({
                        ...filterFreightOffer,
                        termsList: e.target.value,
                      });
                    },
                  }}
                >
                  {incoterms.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  id="outlined-number"
                  label="40 Ft Rate"
                  value={filterFreightOffer?.fortyFtRate || ""}
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      fortyFtRate: e.target.value,
                    })
                  }
                />

                <TextField
                  id="outlined-number"
                  label="20 Ft Rate"
                  value={filterFreightOffer?.twentyFtRate || ""}
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      twentyFtRate: e.target.value,
                    })
                  }
                />

                <TextField
                  id="outlined-number"
                  label="Ftl Rate"
                  value={filterFreightOffer?.thirdFtRate || ""}
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      thirdFtRate: e.target.value,
                    })
                  }
                />

                <TextField
                  id="date"
                  type="date"
                  label="Valid From"
                  value={filterFreightOffer?.validFrom || ""}
                  onKeyDown={(e) => e.preventDefault()}
                  inputProps={{ min: "1999-01-01", max: "2999-01-01" }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      validFrom: e.target.value,
                    })
                  }
                />

                <TextField
                  id="date"
                  type="date"
                  label="Valid To"
                  value={filterFreightOffer?.validTo || ""}
                  onKeyDown={(e) => e.preventDefault()}
                  inputProps={{ min: "1999-01-01", max: "2999-01-01" }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      validTo: e.target.value,
                    })
                  }
                />

                <TextField
                  id="outlined-search"
                  label="Remark"
                  value={filterFreightOffer?.remark || ""}
                  type="search"
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      remark: e.target.value,
                    })
                  }
                />

                <TextField
                  id="outlined-search"
                  label="Carrier"
                  value={filterFreightOffer?.carrier || ""}
                  type="search"
                  onChange={(e) =>
                    setFilterFreightOffer({
                      ...filterFreightOffer,
                      carrier: e.target.value,
                    })
                  }
                />

                <TextField
                  label="Record Status"
                  type="search"
                  id="outlined-select-status"
                  select
                  SelectProps={{
                    multiple: true,
                    value: filterFreightOffer.recordStatusList || [],
                    onChange: (e) => {
                      setFilterFreightOffer({
                        ...filterFreightOffer,
                        recordStatusList: e.target.value,
                      });
                    },
                  }}
                >
                  {recordStatus.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <MyFilters
                  componentName="FreightOfferView"
                  filterState={filterFreightOffer}
                  onApply={(saved) => setFilterFreightOffer(saved)}
                />
                
              </Grid>
              <br></br>
              <Grid
                container
                spacing={2}
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item xs={3} sm={1}>
                  <Button
                    disableElevation
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      retrieveFilteredFreightOffersApi(filterFreightOffer)
                        .then((response) => {
                          setRows(response?.data);
                        })
                        .catch((error) => {
                          console.log(error?.response?.data);
                        });
                    }}
                  >
                    Search
                  </Button>
                </Grid>
                <Grid item xs={3} sm={1}>
                  <Button
                    disableElevation
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setFilterFreightOffer({
                        id: 0,
                        pointOfLoadingList: [],
                        pointOfDischargeList: [],
                        fortyFtRate: null,
                        twentyFtRate: null,
                        thirdFtRate: null,
                        validFrom: "",
                        validTo: "",
                        termsList: [],
                        statusList: [],
                        fortyFtCurrencyList: [],
                        twentyFtCurrencyList: [],
                        thirdFtCurrencyList: [],
                        carrier: "",
                        remark: "",
                        forwarder: "",
                        forwarderList: [],
                        recordStatusList: ["ACTIVE"],
                      });
                    }}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ width: "100%", mt: 2 }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                rows={rows}
                setAddView={setAddView}
              />
              <TableContainer style={{ maxHeight: 700 }}>
                <Table
                  stickyHeader
                  aria-label="sticky table"
                  sx={{
                    minWidth: 750,
                    "& .MuiTableCell-head": {
                      backgroundColor: "#20315d",
                      color: "white",
                    },
                  }}
                  aria-labelledby="tableTitle"
                  size={dense ? "small" : "medium"}
                >
                  <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={rows.length}
                  />
                  <TableBody>
                    {visibleRows.map((row, index) => {
                      const isItemSelected = isSelected(row.id);
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow
                          style={
                            index % 2
                              ? { background: "#f0f0f0" }
                              : { background: "white" }
                          }
                          hover
                          onClick={(event) => handleClick(event, row?.id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row?.id}
                          selected={isItemSelected}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                            />
                          </TableCell>
                          <TableCell padding="normal">
                            <Button
                              onClick={async (e) => {
                                setEditFreightOffer({
                                  visible: true,
                                  data: row,
                                  index: index,
                                });
                              }}
                            >
                              Edit
                            </Button>
                          </TableCell>
                          <TableCell align="center">{row?.id}</TableCell>
                          <TableCell align="center">{row?.forwarder}</TableCell>
                          <TableCell align="center">
                            {row?.pointOfLoading}
                          </TableCell>
                          <TableCell align="center">
                            {row?.pointOfDischarge}
                          </TableCell>
                          <TableCell align="center">{row?.terms}</TableCell>
                          <TableCell align="center">
                            {row?.twentyFtCurrency} {row?.twentyFtRate}
                          </TableCell>
                          <TableCell align="center">
                            {row?.fortyFtCurrency} {row?.fortyFtRate}
                          </TableCell>
                          <TableCell align="center">
                            {row?.fortyHcCurrency} {row?.fortyHcRate}
                          </TableCell>
                          <TableCell align="center">{row?.ftlCurrency} {row?.ftlRate}</TableCell>
                          <TableCell align="center">
                            {row?.validFrom
                              ? format(new Date(row?.validFrom), "dd/MM/yyyy")
                              : ""}
                          </TableCell>
                          <TableCell align="center">
                            {row?.validTo
                              ? format(new Date(row?.validTo), "dd/MM/yyyy")
                              : ""}
                          </TableCell>
                          <TableCell align="center">
                            {row?.transitTime}
                          </TableCell>
                          <TableCell align="center">{row?.remark}</TableCell>
                          <TableCell align="center">{row?.carrier}</TableCell>
                          <TableCell align="center">{row?.status}</TableCell>
                          <TableCell align="center">{row?.timestamp ? format(new Date(row.timestamp), 'dd/MM/yyyy HH:mm'): ''}</TableCell>
                          <TableCell align="center">{row?.creator}</TableCell>
                          <TableCell align="center">
                            {row?.recordStatus}
                          </TableCell>
                          <TableCell align="center">
                            {row?.editStatus}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: (dense ? 33 : 53) * emptyRows,
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
            <FormControlLabel
              control={<Switch checked={dense} onChange={handleChangeDense} />}
              label="Dense padding"
            />
          </Box>
        </>
      )}
    </div>
  );
}